import React, { useState } from "react";
import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSInfoBox,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSControlGroup,
  WSText
} from "@wingspanhq/fe-component-library";
import { IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import { useMutationCancelWorkLogPayable } from "../../../../query/workLog/mutations/useMutationCancelWorkLogPayable";
import { useMutationRequestWorkLogChanges } from "../../../../query/workLog/mutations/useMutationRequestWorkLogChanges";
import { useMutationUpdateWorkLog } from "../../../../query/workLog/mutations/useMutationUpdateWorkLog";
import { PublicCommentField } from "../../components/PublicCommentField";

type ReopenOption = "InReview" | "InProgress";

type FormData = {
  comment: string;
};

type Props = {
  workLog: IWorkLog;
  contractorName?: string;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalReopenWorkLog: React.FC<Props> = ({
  workLog,
  contractorName = "the contractor",
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [cancelPayable, cancelMeta] = useMutationCancelWorkLogPayable();
  const [requestChanges, requestChangesMeta] =
    useMutationRequestWorkLogChanges();
  const [updateWorkLog, updateMeta] = useMutationUpdateWorkLog();
  const [reopenOption, setReopenOption] = useState<ReopenOption>("InReview");

  const isLoading =
    cancelMeta.isLoading ||
    requestChangesMeta.isLoading ||
    updateMeta.isLoading;

  const onSuccess = () => {
    modalProps.onClose({ success: true });
    openSnackbar({
      icon: { name: "check" },
      message:
        reopenOption === "InReview"
          ? "Work log reopened for your edits."
          : "Work log returned to contractor for corrections."
    });
  };

  const onError = () => {
    openSnackbar({
      type: "warning",
      message: "Failed to reopen work log. Please try again."
    });
  };

  const handleReopen = async (formData: FormData) => {
    const comment = formData.comment || undefined;

    // Step 1: Cancel the payable (worklog moves to InReview)
    cancelPayable(
      { invoiceId: workLog.invoiceId! },
      {
        onError,
        onSuccess: () => {
          // Step 2: Transition worklog to final status with optional comment
          if (reopenOption === "InProgress" && comment) {
            requestChanges(
              { workLogId: workLog.workLogId, comment },
              { onSuccess, onError }
            );
          } else if (reopenOption === "InReview" && comment) {
            updateWorkLog(
              {
                workLogId: workLog.workLogId,
                request: { payerOwnedData: { comment } } as any
              },
              { onSuccess, onError }
            );
          } else {
            onSuccess();
          }
        }
      }
    );
  };

  return (
    <WSModal title="Reopen for changes" {...modalProps}>
      <WSForm<FormData> onSubmit={handleReopen} defaultValues={{ comment: "" }}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to cancel the invoice and reopen for changes?
          </WSText.ParagraphSm>

          <WSControlGroup
            type="radio"
            name="reopenOption"
            chip
            value={reopenOption}
            onChange={(value: string) => {
              setReopenOption(value as ReopenOption);
            }}
            options={[
              {
                label: "Make edits yourself",
                helperText:
                  "The invoice will be cancelled. The work log will be reopened for changes and re-approval under the \u201CTo approve\u201D tab in work logs.",
                value: "InReview"
              },
              {
                label: "Return to contractor for corrections",
                helperText:
                  "The invoice will be cancelled. The work log will be returned to contractor for changes and resubmission under the \u201CIn-progress\u201D tab in work logs.",
                value: "InProgress"
              }
            ]}
          />

          <WSInfoBox title="Invoices funded for payment">
            If an invoice has been funded for payment already, the funds are
            returned to the payroll funding account when the invoice is
            cancelled.
          </WSInfoBox>

          <PublicCommentField
            label="Comment (optional)"
            description={`Comment is visible to both you and the contractor. Please provide specific details that will inform ${contractorName} about the decision.`}
            placeholder="Provide specific details about the decision"
          />

          <WSAlert theme="warning" title="Cancel invoice(s)" icon="alert">
            This action cannot be undone
          </WSAlert>

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "Cancel",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                destructive: true,
                label: "Cancel invoice and reopen work log",
                loading: isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalReopenWorkLog = () => useWSModalCustom(ModalReopenWorkLog);
